/**
 * Certification API Service (Public)
 * Endpoint: GET /api/certification/:certificationId/public
 */

import { apiRequest, ApiError } from "./api";

/** @typedef {'NONE'|'VERIFIED'|'TRUSTED'|'IMPACT'|'REGEN'} CertificationLevel */
/** @typedef {'PENDING'|'APPROVED'|'REVOKED'|'EXPIRED'} CertificationStatus */
/** @typedef {'COMPLIANCE'|'SUSTAINABILITY'|'SOCIAL'|'GOVERNANCE'} BadgeCategory */
/** @typedef {'DOCUMENT'|'AUDIT'|'CERTIFICATION'|'INSPECTION'} EvidenceType */
/**
 * @typedef {Object} Badge
 * @property {string} badgeId
 * @property {BadgeCategory} category
 * @property {string} icon
 * @property {string} name
 * @property {string} description
 * @property {string} earnedAt
 */
/**
 * @typedef {Object} Evidence
 * @property {string} evidenceId
 * @property {EvidenceType} type
 * @property {string=} title
 * @property {string=} description
 * @property {string=} questionKey
 * @property {string=} questionText
 * @property {boolean=} isVerified
 * @property {string=} verificationStatus
 * @property {string=} verifiedAt
 * @property {string=} verifiedDate
 */
/**
 * @typedef {Object} EvidenceSummary
 * @property {number=} verified
 */
/**
 * @typedef {Object} PublicCertification
 * @property {string} certificationId
 * @property {string} entityId
 * @property {CertificationLevel} level
 * @property {CertificationStatus} status
 * @property {string} confidence
 * @property {string} issuedAt
 * @property {string} expiresAt
 * @property {Badge[]} badges
 * @property {Evidence[]} evidence
 * @property {EvidenceSummary=} evidenceSummary
 * @property {string|null} ipfsJsonCid
 * @property {string|null} ipfsPdfCid
 * @property {string|null} lastPublishedAt
 * @property {string=} entityName
 * @property {string=} companyName
 * @property {string=} logoUrl
 */

/**
 * Recupera una certificazione pubblica.
 * @param {string} certificationId
 * @returns {Promise<PublicCertification>}
 */
export async function getPublicCertification(certificationId) {
  if (!certificationId || !certificationId.trim()) {
    throw new ApiError("Certification ID non valido", 400, null);
  }

  try {
    return await apiRequest(`/certification/${certificationId}/public`, {
      method: "GET",
    });
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.status === 404) {
        throw new ApiError("Certificazione non trovata", 404, error.data);
      }
      if (error.status >= 500) {
        throw new ApiError("Errore del server durante il recupero della certificazione", error.status, error.data);
      }
    }
    throw error;
  }
}
